/**
 * Edge anchor allocation — shared algorithm for distributing connector
 * endpoints across the four sides of their nodes after a layout pass.
 *
 * Used by:
 *   - RdmLayoutService (RDM hydration / first paint) — mode 'preserve'
 *   - ApplyAutoLayoutCommand (editor Tidy)            — mode 'redistribute'
 *
 * Both paths previously had separate implementations and they drifted:
 * the editor's Tidy command set every offset to a hard-coded `0.5`,
 * which crushed parallel edges (multiple A→B, or bidirectional A↔B)
 * onto the exact same midpoint of each node's side. The RDM hydration
 * path bucketed parallel edges into "lanes" and staggered their offsets
 * via `(i + 1) / (n + 1)` so they rendered as visibly separated lines.
 *
 * This helper centralises the smart algorithm so the two consumers stay
 * in lock-step. The caller supplies a `resolveSides` function (because
 * the editor has access to ELK-computed endpoints for sharper side
 * picks while RDM hydration only has node geometry) and picks a `mode`:
 *
 *   - 'preserve'    — hydration semantics. Pre-existing offsets stay,
 *                     missing offsets get assigned by lane distribution.
 *                     Applied during first paint when manually-placed
 *                     edges may carry user-pinned offsets we shouldn't
 *                     stomp.
 *
 *   - 'redistribute' — Tidy semantics. Every edge gets a fresh offset
 *                      from the lane distribution. The user clicked
 *                      "Tidy" expecting a clean layout; that overrides
 *                      any prior pin (they can re-pin manually after).
 *                      Single-edge lanes still default to 0.5, so an
 *                      edge that's the only one between A and B doesn't
 *                      jump.
 */

export type AnchorSide = 'top' | 'right' | 'bottom' | 'left';

export interface EdgeAnchorPair {
  fromSide: AnchorSide;
  toSide: AnchorSide;
}

export interface EdgeAnchorAllocation {
  fromAnchor: AnchorSide;
  toAnchor: AnchorSide;
  fromOffset: number;
  toOffset: number;
}

interface EdgeShape {
  id: string;
  from: string;
  to: string;
  fromAnchor?: AnchorSide;
  toAnchor?: AnchorSide;
  fromOffset?: number | null;
  toOffset?: number | null;
}

export type EdgeSideResolver<E extends EdgeShape> = (edge: E) => EdgeAnchorPair | null | undefined;

export interface AllocateEdgeAnchorsOptions {
  /**
   * - 'preserve' keeps any non-null `fromOffset` / `toOffset` already
   *   on the edge (RDM hydration path).
   * - 'redistribute' always assigns a fresh offset from the lane
   *   distribution (Tidy path).
   */
  mode: 'preserve' | 'redistribute';
}

/**
 * Compute per-edge anchor allocations.
 *
 * The algorithm is three passes:
 *
 *   1. Per edge, ask the caller's `resolveSides` for an anchor pair.
 *      Edges where it returns null/undefined are skipped — the caller
 *      keeps whatever they had.
 *
 *   2. Bucket edges by an unordered "lane key": `nodeA:sideA|nodeB:sideB`
 *      with the smaller endpoint first. Two edges sharing the same
 *      pair of (node, side) endpoints — bidirectional or parallel —
 *      share a lane and will be staggered against each other.
 *
 *   3. Within each lane, distribute offsets as `(i + 1) / (n + 1)`
 *      so n=1 → [0.5], n=2 → [0.333, 0.667], n=3 → [0.25, 0.5, 0.75].
 *      Both endpoints of an edge get the same offset, which keeps
 *      the line visually consistent across its bend.
 *
 * Returns a Map keyed by edge id. Edges absent from the map were
 * skipped by `resolveSides` and the caller should leave them alone.
 */
export function allocateEdgeAnchors<E extends EdgeShape>(
  edges: readonly E[],
  resolveSides: EdgeSideResolver<E>,
  options: AllocateEdgeAnchorsOptions,
): Map<string, EdgeAnchorAllocation> {
  interface Work {
    edge: E;
    pair: EdgeAnchorPair;
    laneKey: string;
  }

  const work: Work[] = [];
  for (const edge of edges) {
    const pair = resolveSides(edge);
    if (!pair) continue;
    const a = `${edge.from}:${pair.fromSide}`;
    const b = `${edge.to}:${pair.toSide}`;
    const laneKey = a < b ? `${a}|${b}` : `${b}|${a}`;
    work.push({ edge, pair, laneKey });
  }

  const lanes = new Map<string, Work[]>();
  for (const w of work) {
    let bucket = lanes.get(w.laneKey);
    if (!bucket) {
      bucket = [];
      lanes.set(w.laneKey, bucket);
    }
    bucket.push(w);
  }

  const result = new Map<string, EdgeAnchorAllocation>();
  for (const [, group] of lanes) {
    const n = group.length;
    for (let i = 0; i < n; i++) {
      const w = group[i]!;
      const distributedOffset = n === 1 ? 0.5 : (i + 1) / (n + 1);

      const keepFromOffset =
        options.mode === 'preserve' &&
        w.edge.fromOffset !== undefined &&
        w.edge.fromOffset !== null;
      const keepToOffset =
        options.mode === 'preserve' &&
        w.edge.toOffset !== undefined &&
        w.edge.toOffset !== null;

      result.set(w.edge.id, {
        fromAnchor: w.pair.fromSide,
        toAnchor: w.pair.toSide,
        fromOffset: keepFromOffset ? (w.edge.fromOffset as number) : distributedOffset,
        toOffset: keepToOffset ? (w.edge.toOffset as number) : distributedOffset,
      });
    }
  }
  return result;
}
